import React, { useState, useMemo } from "react";
import { Activity, HeartPlus, UserCheck, Trophy, Info } from "lucide-react";
import { getTextEmotionColor, getEmojiImage } from "../utils";
import ParticipationTable from "./ParticipationTable";

export function Trends({ state, emotionPercents, name }) {
  const [showModal, setShowModal] = useState(false);
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formattedCounter = formatNumber(state.actionsCounter);
  const wellnessPracticeChange = useMemo(() => {
    return state.compareActionsCounter > 0
      ? Math.round(
          ((state.actionsCounter - state.compareActionsCounter) /
            state.compareActionsCounter) *
            100,
        )
      : 0;
  }, [state.actionsCounter, state.compareActionsCounter]);

  const { mostIncreasedEmotion, mostDecreasedEmotion } = useMemo(() => {
    const sorted = [...emotionPercents].sort((a, b) => b.change - a.change);
    return {
      mostIncreasedEmotion: sorted[0] || { name: "tired", change: 0 },
      mostDecreasedEmotion: sorted[sorted.length - 1] || {
        name: "stressed",
        change: 0,
      },
    };
  }, [emotionPercents]);

  const { practiced, didNotPractice, percent, percentDifference } =
    useMemo(() => {
      const grouped = {};
      state.filteredDataPoints.forEach((d) => {
        const student = d.email;
        if (!grouped[student]) grouped[student] = [];
        grouped[student].push(d);
      });

      const practiced = Object.entries(grouped).map(
        ([studentEmail, records]) => ({
          studentName:
            state.studentEmailToNameMap[studentEmail] || studentEmail,
          email: studentEmail,
          records,
        }),
      );

      const practicedEmails = new Set(practiced.map((p) => p.email));

      const didNotPractice = state.currentStudents
        .filter((email) => !practicedEmails.has(email))
        .map((email) => ({
          studentName: state.studentEmailToNameMap[email] || email,
          records: [],
        }));

      practiced.sort((a, b) => b.records.length - a.records.length);

      const total = state.currentStudents.length;
      const percent =
        total === 0 ? 0 : Number(((practiced.length / total) * 100).toFixed(1));
      const percentDifference = Number((100 - percent).toFixed(1));

      return { practiced, didNotPractice, percent, percentDifference };
    }, [
      state.filteredDataPoints,
      state.currentStudents,
      state.studentEmailToNameMap,
    ]);

  return (
    <div className="flex-1 bg-white shadow rounded p-3 sm:p-4 2xl:p-8 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-5 h-5 shrink-0" />
        <h3 className="text-[22px] sm:text-[24px] font-bold">
          {" "}
          {name && state.chosenStudent && state.chosenStudent !== "All Students"
            ? `${name}'s Trends`
            : "Trends"}
        </h3>
        <div className="group relative inline-block">
          <Info className="w-4 h-4 text-400 text-[#5b9cd6] hover:text-gray-600 cursor-pointer transition-colors" />
          <div className="absolute hidden group-hover:block right-1/2 sm:right-0 2xl:left-0 mt-2 w-48 sm:w-64 p-2 text-sm bg-gray-100 rounded-md shadow-md z-50">
            <span className="text-400 text-[14px]">
              Click on the participation bar to view the participation of
              students.
            </span>
          </div>
        </div>
      </div>
      <div className="flex sm:flex-row flex-col items-start sm:items-center justify-between gap-2 w-full">
        <div className="flex flex-col items-center w-full sm:w-auto border-b border-[#DDDDDD] py-2 sm:border-none">
          <div className="flex flex-col items-center w-full sm:w-auto gap-1">
            <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start">
              <div className="flex flex-row items-center gap-2">
                <UserCheck className="w-5 h-5" />
                <div className="text-[16px] text-600 sm:hidden opacity-50">
                  PARTICIPATION
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-1">
                {/* Mobile: show change left of percent */}
                <div className="sm:hidden flex flex-row items-center">
                  {state.participationPercentChange !== 0 && (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`w-4 h-4 ${
                          state.participationPercentChange > 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d={
                            state.participationPercentChange > 0
                              ? "M5 10l7-7 7 7M12 3v18"
                              : "M19 14l-7 7-7-7M12 21V3"
                          }
                        />
                      </svg>
                      <div
                        className={`text-[16px] font-bold ml-1 ${
                          state.participationPercentChange > 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {Math.abs(state.participationPercentChange)}%
                      </div>
                    </>
                  )}
                </div>
                <div className="sm:text-[36px] text-[22px] text-700 font-bold">
                  {percent}%
                </div>
                {/* Desktop: show change to the right */}
                <div className="hidden sm:flex flex-row items-center ml-1">
                  {state.participationPercentChange !== 0 && (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`w-4 h-4 ${
                          state.participationPercentChange > 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d={
                            state.participationPercentChange > 0
                              ? "M5 10l7-7 7 7M12 3v18"
                              : "M19 14l-7 7-7-7M12 21V3"
                          }
                        />
                      </svg>
                      <div
                        className={`text-[18px] font-bold ml-1 ${
                          state.participationPercentChange > 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {Math.abs(state.participationPercentChange)}%
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-[16px] text-600 sm:block hidden opacity-50">
              PARTICIPATION
            </div>
            <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
              <div
                className="flex-1 sm:flex-none sm:w-40 h-2.5 bg-gray-200 rounded-full mt-1 cursor-pointer"
                onClick={() => setShowModal(true)}
              >
                <div
                  className="h-2.5 bg-[#FDBA74] rounded-full transition-all duration-300"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <Trophy className="w-4 h-4 text-[#FDBA74]" />
            </div>

            {showModal && (
              <div className="fixed inset-0 z-20 sm:ml-[130px] flex justify-center pt-32 2xl:pt-60 bg-black/20">
                <div className="bg-white rounded shadow-lg w-[95%] sm:w-[500px] xl:w-[700px] max-h-[70vh] sm:max-h-[500px] relative overflow-y-auto">
                  <h3 className="font-bold text-base text-700 pt-3 pb-3 px-3 sm:pl-8 sm:pr-8 uppercase">
                    PARTICIPATION IN THE LAST {state.chosenTime}
                  </h3>
                  <div className="border-b-2 border-[#2D3748]/5"></div>
                  <div className="px-3 sm:pl-8 sm:pr-8 pb-3 pt-3">
                    <div className="flex flex-row items-center gap-1 mb-1">
                      <Trophy className="w-5 h-5 text-[#FECC1B] mr-1" />
                      <p className="text-lg">
                        {percentDifference === 0 &&
                          `Congratulations! Full participation reached!`}
                        {percentDifference > 0 &&
                          percentDifference < 50 &&
                          `Almost there! Only ${percentDifference}% to reach full participation`}
                        {percentDifference >= 50 &&
                          percentDifference <= 100 &&
                          `Keep going! When students practice wellness, everyone wins!`}
                      </p>
                    </div>
                    <div className="flex flex-row items-center gap-2 mb-3">
                      <div
                        className="w-full h-2.5 bg-gray-200 rounded-full cursor-pointer"
                        onClick={() => setShowModal(true)}
                      >
                        <div
                          className="h-2.5 bg-[#FECC1B] rounded-full transition-all duration-300"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="flex flex-row items-center ml-1">
                        {state.participationPercentChange !== 0 && (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`w-4 h-4 ${
                                state.participationPercentChange > 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d={
                                  state.participationPercentChange > 0
                                    ? "M5 10l7-7 7 7M12 3v18"
                                    : "M19 14l-7 7-7-7M12 21V3"
                                }
                              />
                            </svg>
                            <div
                              className={`text-base text-700 font-bold ml-1  ${
                                state.participationPercentChange > 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {Math.abs(state.participationPercentChange)}%
                            </div>
                          </>
                        )}
                      </div>
                      <div className="text-base text-700 font-bold">
                        {percent}%
                      </div>
                    </div>
                    <button
                      className="absolute top-3 sm:right-5 right-2 text-gray-500 text-lg hover:text-black"
                      onClick={() => setShowModal(false)}
                    >
                      ✕
                    </button>
                    <ParticipationTable
                      practiced={practiced}
                      didNotPractice={didNotPractice}
                      nonConsented={state.nonConsented}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center w-full sm:w-auto border-b border-[#DDDDDD] py-2 sm:border-none">
          <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start">
            <div className="flex flex-row items-center gap-2">
              <HeartPlus className="w-5 h-5" />
              <div className="text-[16px] text-600 sm:hidden opacity-50">
                WELLNESS PRACTICES
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-1">
              <div className="sm:hidden flex flex-row items-center">
                {wellnessPracticeChange !== 0 && (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`w-4 h-4 ${
                        wellnessPracticeChange > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d={
                          wellnessPracticeChange > 0
                            ? "M5 10l7-7 7 7M12 3v18"
                            : "M19 14l-7 7-7-7M12 21V3"
                        }
                      />
                    </svg>
                    <div
                      className={`text-[16px] font-bold ml-1 ${
                        wellnessPracticeChange > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {Math.abs(wellnessPracticeChange)}%
                    </div>
                  </>
                )}
              </div>
              <div className="sm:text-[36px] text-[22px] text-700 font-bold">
                {formattedCounter}
              </div>
              <div className="hidden sm:flex flex-row items-center ml-1">
                {wellnessPracticeChange !== 0 && (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`w-4 h-4 ${
                        wellnessPracticeChange > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d={
                          wellnessPracticeChange > 0
                            ? "M5 10l7-7 7 7M12 3v18"
                            : "M19 14l-7 7-7-7M12 21V3"
                        }
                      />
                    </svg>
                    <div
                      className={`text-[18px] font-bold ml-1 ${
                        wellnessPracticeChange > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {Math.abs(wellnessPracticeChange)}%
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="text-[16px] text-600 sm:block hidden opacity-50 mt-1">
            WELLNESS PRACTICES
          </div>
        </div>
        <div className="flex flex-col items-center w-full sm:w-auto border-b border-[#DDDDDD] py-2 sm:border-none">
          <div
            className={`flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start`}
          >
            <div className="flex flex-row items-center gap-2">
              <img
                src={getEmojiImage(mostIncreasedEmotion.name)}
                alt={mostIncreasedEmotion.name}
                className="h-8 w-8"
              />
              <div className="text-[16px] text-600 sm:hidden opacity-50">
                {mostIncreasedEmotion.name?.toUpperCase()} LEVEL
              </div>
            </div>
            <div
              className={`flex flex-row items-center ${getTextEmotionColor(
                mostIncreasedEmotion.name,
              )}`}
            >
              {mostIncreasedEmotion.change !== 0 && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 10l7-7 7 7M12 3v18"
                  />
                </svg>
              )}
              <div
                className={`text-[18px] text-700 font-bold ml-1 ${getTextEmotionColor(
                  mostIncreasedEmotion.name,
                )}`}
              >
                {Math.abs(mostIncreasedEmotion.change)}%
              </div>
            </div>
          </div>
          <div
            className={`text-[16px] text-600 sm:block hidden mt-1 opacity-50`}
          >
            {mostIncreasedEmotion.name?.toUpperCase()} LEVEL
          </div>
        </div>
        <div className="flex flex-col items-center sm:mr-4 w-full sm:w-auto py-2">
          <div
            className={`flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start`}
          >
            <div className="flex flex-row items-center gap-2">
              <img
                src={getEmojiImage(mostDecreasedEmotion.name)}
                alt={mostDecreasedEmotion.name}
                className="h-8 w-8"
              />
              <div className="text-[16px] text-600 sm:hidden opacity-50">
                {mostDecreasedEmotion.name?.toUpperCase()} LEVEL
              </div>
            </div>
            <div
              className={`flex flex-row items-center ${getTextEmotionColor(
                mostDecreasedEmotion.name,
              )}`}
            >
              {mostDecreasedEmotion.change !== 0 && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 14l-7 7-7-7M12 3v18"
                  />
                </svg>
              )}
              <div
                className={`text-[18px] text-700 font-bold ml-1 ${getTextEmotionColor(
                  mostDecreasedEmotion.name,
                )}`}
              >
                {Math.abs(mostDecreasedEmotion.change)}%
              </div>
            </div>
          </div>
          <div
            className={`text-[16px] text-600 sm:block hidden mt-1 opacity-50`}
          >
            {mostDecreasedEmotion.name?.toUpperCase()} LEVEL
          </div>
        </div>
      </div>
    </div>
  );
}
